import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Button from "./Button";
import AddressModal from "./AddressModal";
import { IAddress, ICreateOrderRequest, IOrderItem, api, getNullableString } from "../api/api";
import { useToast } from './ToastContainer';
import { CartItem, SupplyProduct, useSupplyStore } from '../store/supplyStore';

interface SupplyModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: 'search' | 'sizes' | 'cart' | 'checkout';
  product?: {
    id: string;
    name: string;
    brand: string;
    article: string;
    description?: string;
    images: string[];
    sizes: {
      size: string;
      price: number;
      quantity: number;
    }[];
  } | null;
  cart?: CartItem[];
  onOrder?: (size?: string, quantity?: number, price?: number) => void;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  padding: 16px;

  @media (max-width: 768px) {
    padding: 8px;
    align-items: flex-end;
  }

  @media (min-width: 769px) {
    padding: 20px;
  }
`;

const ModalContent = styled.div<{ $isCheckout?: boolean }>`
  background-color: #081c21;
  border-radius: 16px;
  width: 100%;
  max-width: ${props => props.$isCheckout ? '1200px' : '900px'};
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-height: 95vh;
    border-radius: 16px 16px 0 0;
    max-width: none;
    height: auto;
    min-height: 50vh;
  }
`;

const ScrollableContent = styled.div`
  overflow-y: auto;
  flex: 1;
  
  /* Кастомный скроллбар */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  color: #8ca9a9;
  cursor: pointer;
  padding: 12px;
  border-radius: 8px;
  transition: all 0.2s ease;
  z-index: 10;
  min-height: 44px;
  min-width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (min-width: 768px) {
    top: 16px;
    right: 16px;
    padding: 8px;
    min-height: auto;
    min-width: auto;
  }
  
  &:hover {
    color: #ffffff;
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

// Стили для выбора размеров
const ModalHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 20px 16px 0 16px;
  gap: 16px;

  @media (min-width: 768px) {
    padding: 24px 24px 0 24px;
    gap: 20px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const ProductImage = styled.div`
  width: 100px;
  height: 100px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: 120px;
    height: 120px;
  }

  @media (max-width: 768px) {
    width: 140px;
    height: 140px;
  }
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 8px 0;
  line-height: 1.3;

  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

const ProductMeta = styled.div`
  font-size: 16px;
  color: #8ca9a9;
  margin-bottom: 12px;
`;

const ProductArticle = styled.div`
  display: inline-block;
  font-size: 14px;
  color: #24b97d;
  background-color: rgba(36, 185, 125, 0.1);
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid rgba(36, 185, 125, 0.2);
  font-weight: 500;
  margin-bottom: 16px;
`;

const SizesSection = styled.div`
  padding: 16px;

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const SizesTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 20px 0;
`;

const SizesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
  }
`;

const SizeCard = styled.div<{ $selected?: boolean; $available?: boolean }>`
  background-color: ${props => 
    props.$selected ? 'rgba(36, 185, 125, 0.1)' : 
    props.$available ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.02)'
  };
  border: 2px solid ${props => 
    props.$selected ? '#24b97d' : 
    props.$available ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)'
  };
  border-radius: 8px;
  padding: 16px 12px;
  text-align: center;
  cursor: ${props => props.$available ? 'pointer' : 'not-allowed'};
  opacity: ${props => props.$available ? 1 : 0.5};
  transition: all 0.2s ease;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: 768px) {
    padding: 12px;
    min-height: auto;
  }

  &:hover {
    ${props => props.$available && !props.$selected && `
      background-color: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.2);
    `}
  }

  &:active {
    transform: scale(0.98);
  }
`;

const SizeLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 6px;
`;

const SizePrice = styled.div`
  font-size: 16px;
  color: #24b97d;
  font-weight: 600;
  margin-bottom: 6px;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #609f94;
  background-color: transparent;
  color: #609f94;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s ease;
  min-width: 32px;
  min-height: 32px;

  @media (min-width: 768px) {
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    font-size: 14px;
  }

  &:hover:not(:disabled) {
    background-color: rgba(96, 159, 148, 0.1);
    color: #ffffff;
  }

  &:active:not(:disabled) {
    transform: scale(0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.div`
  min-width: 30px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

// Стили для checkout окна
const CheckoutContainer = styled.div`
  display: flex;
  min-height: 0;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CheckoutLeft = styled.div`
  width: 400px;
  background-color: rgba(255, 255, 255, 0.02);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    order: 2;
    max-height: 40vh;
    overflow-y: auto;
  }
`;

const CheckoutRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: 768px) {
    order: 1;
    max-height: 60vh;
  }
`;

const CheckoutHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const CheckoutTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 8px 0;

  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #8ca9a9;
  cursor: pointer;
  padding: 12px 0;
  font-size: 14px;
  margin-bottom: 16px;
  min-height: 44px;
  
  @media (min-width: 768px) {
    padding: 8px 0;
    min-height: auto;
  }

  &:hover {
    color: #ffffff;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const CheckoutContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  
  @media (min-width: 768px) {
    padding: 24px;
  }
  
  /* Кастомный скроллбар */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
`;

const Input = styled.input`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background-color: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #24b97d;
    background-color: rgba(255, 255, 255, 0.08);
  }
  
  &::placeholder {
    color: #8ca9a9;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background-color: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #24b97d;
    background-color: rgba(255, 255, 255, 0.08);
  }
  
  &::placeholder {
    color: #8ca9a9;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const RadioOption = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid ${props => props.$selected ? '#24b97d' : 'rgba(255, 255, 255, 0.1)'};
  background-color: ${props => props.$selected ? 'rgba(36, 185, 125, 0.1)' : 'rgba(255, 255, 255, 0.03)'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  color: ${props => props.$selected ? '#24b97d' : '#ffffff'};
  
  &:hover {
    border-color: #24b97d;
    background-color: rgba(36, 185, 125, 0.05);
  }
`;

const RadioInput = styled.input`
  display: none;
`;

const AddressSelector = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
`;

const AddressInput = styled(Input)`
  flex: 1;
`;

const AddressButton = styled.button`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background-color: rgba(255, 255, 255, 0.05);
  color: #24b97d;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    border-color: #24b97d;
    background-color: rgba(36, 185, 125, 0.1);
  }
`;

// Стили для корзины (правая часть)
const CartSection = styled.div`
  padding: 16px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  @media (min-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    max-height: calc(60vh - 140px);
  }
  
  /* Кастомный скроллбар */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const CartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px 0;
`;

const CartItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 8px;

  @media (min-width: 768px) {
    padding: 16px;
    margin-bottom: 12px;
  }
`;

const CartItemImage = styled.div`
  width: 50px;
  height: 50px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const CartItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const CartItemName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 4px;
`;

const CartItemDetails = styled.div`
  font-size: 12px;
  color: #8ca9a9;
  margin-bottom: 8px;
`;

const CartItemBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CartItemQuantity = styled.div`
  font-size: 12px;
  color: #8ca9a9;
`;

const CartItemPrice = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #24b97d;
`;

const CartItemActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: #ff4444;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  svg {
    transition: opacity 0.2s;
  }

  &:hover {
    background-color: rgba(255, 68, 68, 0.1);
    
    svg path {
      stroke: #ff6666;
    }
  }
`;

const AddressSection = styled.div`
  margin-bottom: 24px;
`;

const AddressTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AddressInfo = styled.div`
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const AddressText = styled.div`
  font-size: 14px;
  color: #ffffff;
  margin-bottom: 8px;
`;

const RecipientText = styled.div`
  font-size: 14px;
  color: #8ca9a9;
`;

const ChangeButton = styled.button`
  background: none;
  border: none;
  color: #24b97d;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  
  &:hover {
    color: #1e9a66;
  }
`;

const CartTotal = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 16px;
  flex-shrink: 0;
  background-color: #081c21;

  @media (min-width: 768px) {
    padding: 24px;
  }

  @media (max-width: 768px) {
    position: sticky;
    bottom: 0;
    z-index: 10;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
  }
`;

const CartTotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: #8ca9a9;
`;

const CartTotalMain = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin-top: 12px;
  margin-bottom: 16px;
`;

const CartTotalAmount = styled.span`
  color: #24b97d;
`;

const CheckoutButton = styled(Button)`
  width: 100%;
`;

const Footer = styled.div`
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  @media (min-width: 768px) {
    padding: 24px;
    gap: 16px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const TotalInfo = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
`;

const TotalAmount = styled.span`
  color: #24b97d;
  margin-left: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    
    button {
      flex: 1;
      min-height: 48px;
    }
  }
`;

// Стили для поиска товаров
const SearchSection = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
`;

const SearchTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const SearchSubtitle = styled.p`
  font-size: 16px;
  color: #8ca9a9;
  margin: 0;
`;

const SearchForm = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  gap: 12px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-size: 16px;
  
  &::placeholder {
    color: #8ca9a9;
  }
  
  &:focus {
    outline: none;
    border-color: #24b97d;
  }
`;

const SearchButton = styled(Button)`
  white-space: nowrap;
`;

// Стили для режима корзины
const CartActions = styled.div`
  padding: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CartButton = styled(Button)`
  width: 100%;
`;

const CartSummary = styled.div`
  padding: 16px;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
  flex-wrap: nowrap;
`;

const SummaryText = styled.div`
  color: #8ca9a9;
  font-size: 14px;
  flex-shrink: 0;
`;

const SummaryAmount = styled.div`
  color: #24b97d;
  font-weight: 600;
  font-size: 18px;
  white-space: nowrap;
`;

const SupplyModal: React.FC<SupplyModalProps> = ({ isOpen, onClose, mode = 'search', product, cart, onOrder }) => {
  const { showSuccess, showError } = useToast();
  
  // Подключаем store для поиска и корзины
  const {
    isSearching,
    searchResults,
    searchProduct,
    clearSearchResults,
    cart: storeCart,
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    getCartTotal,
    getCartItemsCount
  } = useSupplyStore();

  // Состояния компонента
  const [selectedSizes, setSelectedSizes] = useState<Record<string, number>>({});
  const [currentStep, setCurrentStep] = useState<'search' | 'sizes' | 'cart' | 'checkout'>(mode);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentProduct, setCurrentProduct] = useState<SupplyProduct | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [formData, setFormData] = useState({
    deliveryAddress: '',
    deliveryMethod: 'delivery' as 'pickup' | 'delivery'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<IAddress | null>(null);

  // Используем product из пропсов или currentProduct из поиска
  const activeProduct = product || currentProduct;

  // Сбрасываем состояние при открытии модального окна в режиме search
  useEffect(() => {
    if (isOpen && mode === 'search') {
      setCurrentStep('search');
      setSearchQuery('');
      setCurrentProduct(null);
      setSelectedSizes({});
      setHasSearched(false);
      clearSearchResults();
    }
  }, [isOpen, mode, clearSearchResults]);

  // Отслеживаем изменения результатов поиска
  useEffect(() => {
    if (currentStep === 'search' && !isSearching && hasSearched) {
      if (searchResults.length > 0) {
        setCurrentProduct(searchResults[0]);
        setCurrentStep('sizes');
        setSelectedSizes({}); // Сброс выбранных размеров
        setHasSearched(false); // Сбрасываем флаг после успешного поиска
      } else if (searchQuery.trim()) {
        // Показываем ошибку только если был явный поиск и нет результатов
        showError('Товар не найден');
        setHasSearched(false); // Сбрасываем флаг после показа ошибки
      }
    }
  }, [searchResults, isSearching, currentStep, hasSearched, searchQuery, showError]);

  const handleSizeSelect = (size: string) => {
    setSelectedSizes(prev => ({
      ...prev,
      [size]: prev[size] ? 0 : 1
    }));
  };

  const handleQuantityChange = (size: string, delta: number) => {
    setSelectedSizes(prev => {
      const currentQuantity = prev[size] || 0;
      const newQuantity = Math.max(0, currentQuantity + delta);
      const maxQuantity = 99;
      return {
        ...prev,
        [size]: Math.min(newQuantity, maxQuantity)
      };
    });
  };

  // Новые функции для работы с поиском и корзиной
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setHasSearched(true); // Устанавливаем флаг что поиск был инициирован
      await searchProduct(searchQuery);
      // Результат обрабатывается в useEffect
    } catch (error) {
      showError('Ошибка поиска товара');
      setHasSearched(false); // Сбрасываем флаг при ошибке
    }
  };

  const handleAddToCart = () => {
    if (!activeProduct) return;
    
    let addedAny = false;
    Object.entries(selectedSizes).forEach(([size, quantity]) => {
      if (quantity > 0) {
        // Добавляем category если его нет
        const productWithCategory = {
          ...activeProduct,
          category: 'Кроссовки'
        } as SupplyProduct;
        addToCart(productWithCategory, size, quantity);
        addedAny = true;
      }
    });
    
    if (addedAny) {
      showSuccess('Товары добавлены в корзину');
      setSelectedSizes({});
      setCurrentStep('cart'); // Переключаемся на просмотр корзины
    }
  };

  const handleSearchNew = () => {
    setCurrentStep('search');
    setSearchQuery('');
    setCurrentProduct(null);
    setSelectedSizes({});
    clearSearchResults();
  };

  const handleGoToCheckout = () => {
    setCurrentStep('checkout');
  };

  // Функции для работы с адресами
  const handleOpenAddressModal = () => {
    setIsAddressModalOpen(true);
  };

  const handleAddressSelect = (address: IAddress) => {
    setSelectedAddress(address);
    setIsAddressModalOpen(false);
  };

  // Функции для работы с количеством товаров в корзине
  const handleIncreaseQuantity = (itemId: string) => {
    const item = storeCart.find(item => item.id === itemId);
    if (item) {
      updateCartItem(itemId, item.quantity + 1);
    }
  };

  const handleDecreaseQuantity = (itemId: string) => {
    const item = storeCart.find(item => item.id === itemId);
    if (item) {
      if (item.quantity > 1) {
        updateCartItem(itemId, item.quantity - 1);
      } else {
        removeFromCart(itemId);
      }
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBackToSizes = () => {
    setCurrentStep('cart');
  };

  const handleClose = () => {
    setCurrentStep(mode);
    setSearchQuery('');
    setCurrentProduct(null);
    setSelectedSizes({});
    clearSearchResults();
    onClose();
  };

  const getTotalAmount = () => {
    if (currentStep === 'checkout') {
      // При checkout используем корзину из store
      return getCartTotal();
    }
    if (cart) {
      return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    }
    return Object.entries(selectedSizes).reduce((total, [size, quantity]) => {
      const sizeInfo = activeProduct?.sizes.find(s => s.size === size);
      return total + (sizeInfo?.price || 0) * quantity;
    }, 0);
  };

  const getTotalItems = () => {
    if (currentStep === 'checkout') {
      // При checkout используем корзину из store
      return getCartItemsCount();
    }
    if (cart) {
      return cart.reduce((total, item) => total + item.quantity, 0);
    }
    return Object.values(selectedSizes).reduce((total, quantity) => total + quantity, 0);
  };

  const getOrderItems = (): Array<{
    size: string;
    quantity: number;
    price: number;
    article?: string;
    name?: string;
    brand?: string;
    image?: string;
  }> => {
    if (currentStep === 'checkout') {
      // При checkout используем корзину из store
      return storeCart.map(item => ({
        size: item.size,
        quantity: item.quantity,
        price: item.price,
        article: item.article,
        name: item.name,
        brand: item.brand,
        image: item.image
      }));
    }
    if (cart) {
      return cart.map(item => ({
        size: item.size,
        quantity: item.quantity,
        price: item.price,
        article: item.article,
        name: item.name,
        brand: item.brand,
        image: item.image
      }));
    }
    if (currentStep === 'cart') {
      // При просмотре корзины используем storeCart
      return storeCart.map(item => ({
        size: item.size,
        quantity: item.quantity,
        price: item.price,
        article: item.article,
        name: item.name,
        brand: item.brand,
        image: item.image
      }));
    }
    return Object.entries(selectedSizes)
      .filter(([, quantity]) => quantity > 0)
      .map(([size, quantity]) => {
        const sizeInfo = activeProduct?.sizes.find(s => s.size === size);
        return {
          size,
          quantity,
          price: sizeInfo?.price || 0,
          article: activeProduct?.article,
          name: activeProduct?.name,
          brand: activeProduct?.brand,
          image: activeProduct?.images[0]
        };
      });
  };

  const handleProceedToCheckout = () => {
    const hasSelectedItems = Object.values(selectedSizes).some(quantity => quantity > 0);
    if (hasSelectedItems) {
      setCurrentStep('checkout');
    }
  };

  const handleSelectAddress = (address: IAddress) => {
    setSelectedAddress(address);
    setFormData(prev => ({
      ...prev,
      deliveryAddress: address.address_name
    }));
    setIsAddressModalOpen(false);
  };

  const handleConfirmOrder = async () => {
    if (formData.deliveryMethod === 'delivery' && !selectedAddress) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      let orderItems: IOrderItem[] = [];
      
      if (currentStep === 'checkout' && storeCart.length > 0) {
        // Работаем с корзиной из store
        orderItems = storeCart.map(item => ({
          article: item.article,
          size_eu: parseFloat(item.size),
          count: item.quantity
        }));
      } else {
        // Работаем с одним продуктом
        orderItems = Object.entries(selectedSizes)
          .filter(([, quantity]) => quantity > 0)
          .map(([size, quantity]) => {
            return {
              article: product?.article || '',
              size_eu: parseFloat(size),
              count: quantity
            };
          });
      }

      const orderData: ICreateOrderRequest = {
        items: orderItems,
        address_id: selectedAddress?.Id || null
      };
      
      // Создаем заказ через API
      const result = await api.createOrder(orderData);
      
      // Проверяем успешность создания заказа
      if (result.order_id) {
        showSuccess('Заказ создан', `Заказ успешно создан! ID заказа: ${result.order_id}`);
        
        // Очищаем корзину после успешного заказа
        if (currentStep === 'checkout') {
          clearCart();
        }
        
        // Закрываем модальное окно и сбрасываем состояние
        onClose();
        setSelectedSizes({});
        setCurrentStep(mode);
        setSelectedAddress(null);
        setFormData({
          deliveryAddress: '',
          deliveryMethod: 'delivery'
        });
        
        // Вызываем callback для завершения заказа
        onOrder?.();
        
      } else if (result.error) {
        showError('Ошибка создания заказа', result.error);
        return;
      } else {
        showError('Ошибка', 'Произошла ошибка при создании заказа');
        return;
      }
      
    } catch (error) {
      showError('Ошибка', 'Произошла ошибка при создании заказа');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return formData.deliveryMethod === 'pickup' || selectedAddress !== null;
  };



  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleClose}>
      <ModalContent $isCheckout={currentStep === 'checkout'} onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </CloseButton>

        {/* Режим поиска товаров */}
        {currentStep === 'search' && (
          <SearchSection>
            <SearchTitle>Поиск товаров</SearchTitle>
            <SearchSubtitle>Введите артикул товара для добавления в закупку</SearchSubtitle>
            
            <SearchForm>
              <SearchInput
                type="text"
                placeholder="Введите артикул..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <SearchButton
                variant="primary"
                onClick={handleSearch}
                disabled={isSearching || !searchQuery.trim()}
              >
                {isSearching ? 'Поиск...' : 'Найти'}
              </SearchButton>
            </SearchForm>

            {storeCart.length > 0 && (
              <CartSummary>
                <SummaryText>В корзине {getCartItemsCount()} товаров</SummaryText>
                <SummaryAmount>{getCartTotal().toLocaleString()} ¥</SummaryAmount>
              </CartSummary>
            )}

            {storeCart.length > 0 && (
              <CartActions>
                <CartButton variant="secondary" onClick={() => setCurrentStep('cart')}>
                  Посмотреть корзину
                </CartButton>
                <CartButton variant="primary" onClick={handleGoToCheckout}>
                  Перейти к оформлению
                </CartButton>
              </CartActions>
            )}
          </SearchSection>
        )}

        {/* Режим выбора размеров */}
        {currentStep === 'sizes' && (
          <>
            <ScrollableContent>
              <ModalHeader>
                <ProductImage 
                  style={{ 
                    backgroundImage: activeProduct?.images[0] ? `url(${activeProduct.images[0]})` : 'none'
                  }} 
                />
                <ProductInfo>
                  <ProductName>{activeProduct?.name}</ProductName>
                  <ProductMeta>{activeProduct?.brand}</ProductMeta>
                  <ProductArticle>{activeProduct?.article}</ProductArticle>
                </ProductInfo>
              </ModalHeader>

              <SizesSection>
                <SizesTitle>Выберите размеры и количество</SizesTitle>
                <SizesGrid>
                  {activeProduct?.sizes.map((sizeInfo, index) => {
                    const isSelected = (selectedSizes[sizeInfo.size] || 0) > 0;
                    const isAvailable = true;
                    const quantity = selectedSizes[sizeInfo.size] || 0;

                    return (
                      <SizeCard 
                        key={index}
                        $selected={isSelected}
                        $available={isAvailable}
                        onClick={() => isAvailable && handleSizeSelect(sizeInfo.size)}
                      >
                        <SizeLabel>EU {sizeInfo.size}</SizeLabel>
                        <SizePrice>{sizeInfo.price.toLocaleString()} ¥</SizePrice>
                        
                        {isAvailable && (
                          <>
                            <QuantityControls>
                              <QuantityButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuantityChange(sizeInfo.size, -1);
                                }}
                                disabled={quantity === 0}
                              >
                                −
                              </QuantityButton>
                              <QuantityDisplay>{quantity}</QuantityDisplay>
                              <QuantityButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleQuantityChange(sizeInfo.size, 1);
                                }}
                                disabled={quantity >= 99}
                              >
                                +
                              </QuantityButton>
                            </QuantityControls>
                          </>
                        )}
                      </SizeCard>
                    );
                  })}
                </SizesGrid>
              </SizesSection>
            </ScrollableContent>

            <Footer>
              <TotalInfo>
                Итого: <TotalAmount>{getTotalAmount().toLocaleString()} ¥</TotalAmount>
                {getTotalItems() > 0 && (
                  <div style={{ fontSize: '14px', color: '#8ca9a9', marginTop: '4px' }}>
                    {getTotalItems()} {getTotalItems() === 1 ? 'товар' : getTotalItems() < 5 ? 'товара' : 'товаров'}
                  </div>
                )}
              </TotalInfo>
              <ButtonGroup>
                <Button 
                  variant="secondary" 
                  onClick={handleSearchNew}
                >
                  Найти новый товар
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleAddToCart}
                  disabled={getTotalItems() === 0}
                >
                  Добавить в корзину
                </Button>
              </ButtonGroup>
            </Footer>
          </>
        )}

        {/* Режим просмотра корзины */}
        {currentStep === 'cart' && (
          <>
            <ScrollableContent>
              <ModalHeader>
                <SearchTitle>Корзина</SearchTitle>
                <SearchSubtitle>Проверьте выбранные товары</SearchSubtitle>
              </ModalHeader>

              <CartSection>
                {storeCart.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#8ca9a9' }}>
                    Корзина пуста
                  </div>
                ) : (
                  storeCart.map((item, index) => (
                    <CartItem key={index}>
                      <CartItemImage 
                        style={{ 
                          backgroundImage: item.image ? `url(${item.image})` : 'none'
                        }} 
                      />
                      <CartItemInfo>
                        <CartItemName>{item.name}</CartItemName>
                        <CartItemDetails>
                          {item.brand} • Размер EU {item.size} • {item.article}
                        </CartItemDetails>
                        <CartItemBottom>
                          <CartItemPrice>
                            {(item.price * item.quantity).toLocaleString()} ¥
                          </CartItemPrice>
                        </CartItemBottom>
                      </CartItemInfo>
                      <CartItemActions>
                        <QuantityControls>
                          <QuantityButton
                            onClick={() => handleDecreaseQuantity(item.id)}
                          >
                            -
                          </QuantityButton>
                          <QuantityDisplay>{item.quantity}</QuantityDisplay>
                          <QuantityButton
                            onClick={() => handleIncreaseQuantity(item.id)}
                          >
                            +
                          </QuantityButton>
                        </QuantityControls>
                        <DeleteButton
                          onClick={() => removeFromCart(item.id)}
                          title="Удалить товар"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_4418_6145)">
                              <path d="M21 5.98047C17.67 5.65047 14.32 5.48047 10.98 5.48047C9 5.48047 7.02 5.58047 5.04 5.78047L3 5.98047" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              <path opacity="0.34" d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M18.8499 9.14062L18.1999 19.2106C18.0899 20.7806 17.9999 22.0006 15.2099 22.0006H8.7899C5.9999 22.0006 5.9099 20.7806 5.7999 19.2106L5.1499 9.14062" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              <path opacity="0.34" d="M10.3301 16.5H13.6601" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              <path opacity="0.34" d="M9.5 12.5H14.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </g>
                            <defs>
                              <clipPath id="clip0_4418_6145">
                                <rect width="24" height="24" fill="white"/>
                              </clipPath>
                            </defs>
                          </svg>
                        </DeleteButton>
                      </CartItemActions>
                    </CartItem>
                  ))
                )}
              </CartSection>
            </ScrollableContent>

            <Footer>
              <TotalInfo>
                Итого: <TotalAmount>{getCartTotal().toLocaleString()} ¥</TotalAmount>
                <div style={{ fontSize: '14px', color: '#8ca9a9', marginTop: '4px' }}>
                  {getCartItemsCount()} товаров в корзине
                </div>
              </TotalInfo>
              <ButtonGroup>
                <Button 
                  variant="secondary" 
                  onClick={handleSearchNew}
                >
                  Добавить товар
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleGoToCheckout}
                  disabled={storeCart.length === 0}
                >
                  Перейти к оформлению
                </Button>
              </ButtonGroup>
            </Footer>
          </>
        )}

        {/* Режим оформления заказа */}
        {currentStep === 'checkout' && (
          <CheckoutContainer>
            <CheckoutRight>
              <CartSection>
                <CartTitle>Корзина</CartTitle>
                {getOrderItems().map((item, index) => (
                  <CartItem key={index}>
                    <CartItemImage 
                      style={{ 
                        backgroundImage: item.image ? `url(${item.image})` : 'none'
                      }} 
                    />
                    <CartItemInfo>
                      <CartItemName>{item.name}</CartItemName>
                      <CartItemDetails>
                        {item.brand} • Размер EU {item.size} • {item.article}
                      </CartItemDetails>
                      <CartItemBottom>
                        <CartItemQuantity>{item.quantity} шт.</CartItemQuantity>
                        <CartItemPrice>
                          {(item.price * item.quantity).toLocaleString()} ¥
                        </CartItemPrice>
                      </CartItemBottom>
                    </CartItemInfo>
                  </CartItem>
                ))}
              </CartSection>

              <CartTotal>
                <CartTotalRow>
                  <span>Товары ({getTotalItems()} шт.)</span>
                  <span>{getTotalAmount().toLocaleString()} ¥</span>
                </CartTotalRow>
                <CartTotalMain>
                  <span>Итого:</span>
                  <CartTotalAmount>{getTotalAmount().toLocaleString()} ¥</CartTotalAmount>
                </CartTotalMain>
                <CheckoutButton 
                  variant="primary" 
                  onClick={handleConfirmOrder}
                  disabled={!isFormValid() || isSubmitting}
                >
                  {isSubmitting ? 'Оформление...' : 'Оформить заказ'}
                </CheckoutButton>
              </CartTotal>
            </CheckoutRight>

            <CheckoutLeft>
              <CheckoutHeader>
                <BackButton onClick={handleBackToSizes}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Назад к выбору размеров
                </BackButton>
                <CheckoutTitle>Оформление заказа</CheckoutTitle>
              </CheckoutHeader>

              <CheckoutContent>
                <FormSection>
                  <SectionTitle>Способ получения</SectionTitle>
                  <RadioGroup>
                    <RadioOption $selected={formData.deliveryMethod === 'pickup'}>
                      <RadioInput
                        type="radio"
                        name="deliveryMethod"
                        value="pickup"
                        checked={formData.deliveryMethod === 'pickup'}
                        onChange={(e) => handleInputChange('deliveryMethod', e.target.value)}
                      />
                      Самовывоз
                    </RadioOption>
                    <RadioOption $selected={formData.deliveryMethod === 'delivery'}>
                      <RadioInput
                        type="radio"
                        name="deliveryMethod"
                        value="delivery"
                        checked={formData.deliveryMethod === 'delivery'}
                        onChange={(e) => handleInputChange('deliveryMethod', e.target.value)}
                      />
                      Доставка
                    </RadioOption>
                  </RadioGroup>
                  
                  {formData.deliveryMethod === 'delivery' && (
                    <FormGroup style={{ marginTop: '16px' }}>
                      <Label>Адрес доставки *</Label>
                      <AddressSelector>
                        <AddressInput
                          type="text"
                          placeholder="Выберите адрес доставки"
                          value={selectedAddress ? (selectedAddress.address_name || 'Адрес не указан') : ''}
                          readOnly
                        />
                        <AddressButton onClick={handleOpenAddressModal}>
                          {selectedAddress ? 'Изменить адрес' : 'Выбрать адрес'}
                        </AddressButton>
                      </AddressSelector>
                      
                      {selectedAddress && (
                        <div style={{ 
                          marginTop: '8px', 
                          padding: '12px', 
                          background: 'rgba(255, 255, 255, 0.05)', 
                          borderRadius: '8px',
                          fontSize: '14px',
                          color: '#8ca9a9'
                        }}>
                          <div><strong>Получатель:</strong> {getNullableString(selectedAddress.first_name)} {getNullableString(selectedAddress.last_name)} {getNullableString(selectedAddress.middle_name)}</div>
                          <div><strong>Адрес:</strong> {selectedAddress.address_name}</div>
                        </div>
                      )}
                    </FormGroup>
                  )}
                </FormSection>

                {formData.deliveryMethod === 'delivery' && selectedAddress && (
                  <FormSection>
                    <SectionTitle>Информация о доставке</SectionTitle>
                    <AddressInfo>
                      <AddressText>
                        {selectedAddress.address_name}
                      </AddressText>
                      <RecipientText>
                        Получатель: {`${getNullableString(selectedAddress.last_name)} ${getNullableString(selectedAddress.first_name)} ${getNullableString(selectedAddress.middle_name)}`.trim()}
                      </RecipientText>
                    </AddressInfo>
                  </FormSection>
                )}
              </CheckoutContent>
            </CheckoutLeft>
          </CheckoutContainer>
        )}
      </ModalContent>
      
      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        onSelectAddress={handleAddressSelect}
        selectedAddressId={selectedAddress?.Id}
      />
    </ModalOverlay>
  );
};

export default SupplyModal;
